import { useState } from 'react'
import { CheckCircle2 } from 'lucide-react'

interface Props {
  confirmationNumber: string
  onSave: (number: string) => void
  isSaving: boolean
  saveSuccess: boolean
  submittedAt?: string
}

function formatSubmittedDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

const NilGoConfirmationInput = ({
  confirmationNumber,
  onSave,
  isSaving,
  saveSuccess,
  submittedAt,
}: Props) => {
  const [inputValue, setInputValue] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [saveError, setSaveError] = useState(false)

  const handleSave = () => {
    if (!inputValue.trim()) return
    setSaveError(false)
    onSave(inputValue.trim())
  }

  const handleNoNumber = () => {
    setSaveError(false)
    onSave('IN_PROGRESS')
  }

  const handleEdit = () => {
    setInputValue(confirmationNumber === 'IN_PROGRESS' ? '' : confirmationNumber)
    setIsEditing(true)
  }

  const isConfirmed = confirmationNumber && !isEditing
  const isInProgress = confirmationNumber === 'IN_PROGRESS' && !isEditing

  return (
    <div className="bg-white/10 border border-white/10 rounded-2xl p-5 space-y-4">
      <p className="text-white font-semibold text-sm">NIL Go Confirmation Number</p>

      {isConfirmed && !isInProgress ? (
        /* Saved state */
        <div className="bg-[#C0FD72]/8 border border-[#C0FD72]/20 rounded-xl px-4 py-4 space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-[#C0FD72]" />
            <p className="text-[#C0FD72] font-semibold text-sm">NIL Go submission confirmed</p>
          </div>
          <p className="font-mono text-white text-base tracking-wide">{confirmationNumber}</p>
          {submittedAt && (
            <p className="text-white/40 text-xs">Submitted {formatSubmittedDate(submittedAt)}</p>
          )}
          <button
            onClick={handleEdit}
            className="text-white/40 hover:text-white/70 text-xs underline underline-offset-2 transition-colors"
          >
            Edit
          </button>
        </div>
      ) : isInProgress ? (
        /* In-progress state (submitted without number) */
        <div className="bg-[#F9DA4C]/8 border border-[#F9DA4C]/20 rounded-xl px-4 py-4 space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-[#F9DA4C]" />
            <p className="text-[#F9DA4C] font-semibold text-sm">
              Submitted — awaiting confirmation number
            </p>
          </div>
          <p className="text-white/50 text-xs">
            Return here and paste your NIL Go confirmation number once you receive it.
          </p>
          {submittedAt && (
            <p className="text-white/40 text-xs">Submitted {formatSubmittedDate(submittedAt)}</p>
          )}
          <button
            onClick={handleEdit}
            className="text-white/40 hover:text-white/70 text-xs underline underline-offset-2 transition-colors"
          >
            Add confirmation number
          </button>
        </div>
      ) : (
        /* Input state */
        <div className="space-y-3">
          <p className="text-white/50 text-sm">
            After submitting on NIL Go, paste your confirmation number here to complete your
            compliance record.
          </p>

          <div className="space-y-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="e.g. NILGO-2026-XXXXXXXX"
              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white text-base placeholder:text-white/25 outline-hidden focus:border-white/40 font-mono"
            />
            <button
              onClick={handleSave}
              disabled={!inputValue.trim() || isSaving}
              className="w-full bg-[#C0FD72] hover:bg-[#b0ef62] disabled:opacity-40 disabled:cursor-not-allowed text-black text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"
            >
              {isSaving ? 'Saving…' : 'Save Confirmation'}
            </button>
          </div>

          {saveError && <p className="text-[#FFA07A] text-xs">Failed to save — try again.</p>}

          {saveSuccess && <p className="text-[#C0FD72] text-xs">Saved successfully.</p>}

          <button
            onClick={handleNoNumber}
            className="text-white/40 hover:text-white/60 text-xs underline underline-offset-2 transition-colors"
          >
            I've submitted but don't have a number yet →
          </button>
        </div>
      )}
    </div>
  )
}

export default NilGoConfirmationInput
